/**
 * 全局WebSocket连接管理器
 * 解决多页面重复连接问题
 */

class WebSocketManager {
  constructor() {
    // 连接池：按URL存储WebSocket连接
    this.connections = new Map();
    this.pending = new Map();
    
    // 订阅者管理：按URL存储订阅者列表
    this.subscribers = new Map();
    
    // 连接状态监听器
    this.statusListeners = new Map();
    
    // 调试模式
    this.debug = false;
  }

  /**
   * 获取或创建WebSocket连接
   * @param {string} url WebSocket URL
   * @param {object} options 连接选项
   * @returns {Promise} 连接Promise
   */
  async getConnection(url, options = {}) {
    if (this.pending.has(url)) {
      return this.pending.get(url);
    }

    if (this.connections.has(url)) {
      const connection = this.connections.get(url);
      const state = connection.ws ? connection.ws.readyState : -1;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
        this.log(`🔄 [WebSocketManager] 复用现有连接: ${url}`);
        return connection;
      }
      this.log(`🧹 [WebSocketManager] 清理断开的连接: ${url}`);
      this.cleanup(url);
    }

    return this.createConnection(url, options);
  }

  /**
   * 创建新的WebSocket连接
   * @param {string} url WebSocket URL
   * @param {object} options 连接选项
   * @returns {Promise} 连接Promise
   */
  createConnection(url, options = {}) {
    if (this.pending.has(url)) {
      return this.pending.get(url);
    }

    const promise = new Promise((resolve, reject) => {
      this.log(`🔌 [WebSocketManager] 创建新连接: ${url}`);
      
      const ws = new WebSocket(url);
      const connection = {
        ws,
        url,
        isConnected: false,
        subscribers: new Set(),
        lastActivity: Date.now(),
        options
      };
      this.connections.set(url, connection);

      ws.onopen = () => {
        connection.isConnected = true;
        this.pending.delete(url);
        this.log(`✅ [WebSocketManager] 连接成功: ${url}`);
        this.notifyStatusChange(url, 'connected');
        resolve(connection);
      };

      ws.onmessage = (event) => {
        connection.lastActivity = Date.now();
        this.broadcastMessage(url, event.data);
      };

      ws.onclose = (event) => {
        connection.isConnected = false;
        this.pending.delete(url);
        this.log(`❌ [WebSocketManager] 连接关闭: ${url}, code: ${event.code}`);
        this.notifyStatusChange(url, 'disconnected');
        this.cleanup(url);
      };

      ws.onerror = (error) => {
        this.log(`🚨 [WebSocketManager] 连接错误: ${url}`, error);
        this.notifyStatusChange(url, 'error');
        reject(error);
      };
    });

    this.pending.set(url, promise);
    promise.catch(() => {}).finally(() => {
      if (this.pending.get(url) === promise) {
        this.pending.delete(url);
      }
    });
    return promise;
  }

  /**
   * 订阅WebSocket消息
   * @param {string} url WebSocket URL
   * @param {string} subscriberId 订阅者ID
   * @param {function} messageHandler 消息处理函数
   * @param {function} statusHandler 状态变化处理函数
   * @returns {Promise} 订阅Promise
   */
  async subscribe(url, subscriberId, messageHandler, statusHandler = null) {
    try {
      // 获取或创建连接
      const connection = await this.getConnection(url);
      
      Array.from(connection.subscribers).forEach((sub) => {
        if (sub.id === subscriberId) {
          connection.subscribers.delete(sub);
        }
      });
      connection.subscribers.add({
        id: subscriberId,
        messageHandler,
        statusHandler
      });

      if (connection.isConnected && typeof statusHandler === 'function') {
        statusHandler('connected');
      }

      this.log(`📡 [WebSocketManager] 添加订阅者: ${subscriberId} → ${url}`);
      
      return {
        connection,
        unsubscribe: () => this.unsubscribe(url, subscriberId)
      };
      
    } catch (error) {
      this.log(`❌ [WebSocketManager] 订阅失败: ${subscriberId} → ${url}`, error);
      throw error;
    }
  }

  /**
   * 取消订阅
   * @param {string} url WebSocket URL
   * @param {string} subscriberId 订阅者ID
   */
  unsubscribe(url, subscriberId) {
    const connection = this.connections.get(url);
    if (!connection) return;

    // 移除订阅者
    connection.subscribers = new Set(
      Array.from(connection.subscribers).filter(sub => sub.id !== subscriberId)
    );

    this.log(`📡 [WebSocketManager] 移除订阅者: ${subscriberId} ← ${url}`);

    // 如果没有订阅者了，关闭连接
    if (connection.subscribers.size === 0) {
      this.log(`🔌 [WebSocketManager] 无订阅者，关闭连接: ${url}`);
      this.closeConnection(url);
    }
  }

  /**
   * 发送消息
   * @param {string} url WebSocket URL
   * @param {string} message 消息内容
   * @returns {boolean} 发送是否成功
   */
  send(url, message) {
    const connection = this.connections.get(url);
    if (!connection || !connection.isConnected) {
      this.log(`❌ [WebSocketManager] 发送失败，连接不可用: ${url}`);
      return false;
    }

    try {
      connection.ws.send(message);
      this.log(`📤 [WebSocketManager] 发送消息: ${url} → ${message.substring(0, 100)}`);
      return true;
    } catch (error) {
      this.log(`❌ [WebSocketManager] 发送消息异常: ${url}`, error);
      return false;
    }
  }

  /**
   * 广播消息给所有订阅者
   * @param {string} url WebSocket URL
   * @param {string} data 消息数据
   */
  broadcastMessage(url, data) {
    const connection = this.connections.get(url);
    if (!connection) return;

    connection.subscribers.forEach(subscriber => {
      try {
        subscriber.messageHandler(data);
      } catch (error) {
        this.log(`❌ [WebSocketManager] 消息处理异常: ${subscriber.id}`, error);
      }
    });
  }

  /**
   * 通知状态变化
   * @param {string} url WebSocket URL
   * @param {string} status 状态
   */
  notifyStatusChange(url, status) {
    const connection = this.connections.get(url);
    if (!connection) return;

    connection.subscribers.forEach(subscriber => {
      if (subscriber.statusHandler) {
        try {
          subscriber.statusHandler(status);
        } catch (error) {
          this.log(`❌ [WebSocketManager] 状态处理异常: ${subscriber.id}`, error);
        }
      }
    });
  }

  /**
   * 强制关闭连接（不触发自动重连，供外部主动重连用）
   * @param {string} url WebSocket URL
   */
  forceCloseConnection(url) {
    const connection = this.connections.get(url);
    if (!connection) {
      this.pending.delete(url);
      return;
    }

    if (connection.ws) {
      connection.ws.onopen = () => {};
      connection.ws.onmessage = () => {};
      connection.ws.onerror = () => {};
      connection.ws.onclose = () => {};
      try {
        connection.ws.close(1000, 'Force reconnect');
      } catch (e) {
        // ignore
      }
    }
    this.cleanup(url);
  }

  /**
   * 关闭连接
   * @param {string} url WebSocket URL
   */
  closeConnection(url) {
    const connection = this.connections.get(url);
    if (connection && connection.ws) {
      connection.ws.close(1000, 'Manager close');
    }
    this.cleanup(url);
  }

  /**
   * 清理连接资源
   * @param {string} url WebSocket URL
   */
  cleanup(url) {
    this.connections.delete(url);
    this.subscribers.delete(url);
    this.pending.delete(url);
    this.log(`🧹 [WebSocketManager] 清理连接资源: ${url}`);
  }

  /**
   * 获取连接状态
   * @param {string} url WebSocket URL
   * @returns {object} 连接状态信息
   */
  getConnectionStatus(url) {
    const connection = this.connections.get(url);
    if (!connection) {
      return { connected: false, subscribers: 0 };
    }

    return {
      connected: connection.isConnected,
      subscribers: connection.subscribers.size,
      lastActivity: connection.lastActivity,
      readyState: connection.ws ? connection.ws.readyState : -1
    };
  }

  /**
   * 获取所有连接状态
   * @returns {object} 所有连接状态
   */
  getAllConnectionStatus() {
    const status = {};
    this.connections.forEach((connection, url) => {
      status[url] = this.getConnectionStatus(url);
    });
    return status;
  }

  /**
   * 启用调试模式
   * @param {boolean} enabled 是否启用
   */
  setDebug(enabled) {
    this.debug = enabled;
  }

  /**
   * 日志输出
   * @param {string} message 日志消息
   * @param {any} data 附加数据
   */
  log(message, data = null) {
    if (this.debug) {
      if (data) {
        console.log(message, data);
      } else {
        console.log(message);
      }
    }
  }

  /**
   * 销毁管理器
   */
  destroy() {
    this.log(`🔥 [WebSocketManager] 销毁管理器`);
    
    // 关闭所有连接
    this.connections.forEach((connection, url) => {
      this.closeConnection(url);
    });
    
    // 清理所有资源
    this.connections.clear();
    this.subscribers.clear();
    this.statusListeners.clear();
  }
}

// 创建全局单例
const wsManager = new WebSocketManager();

// 开发环境启用调试
if (process.env.NODE_ENV === 'development') {
  wsManager.setDebug(true);
}

export default wsManager;
